// src/utilities/api.js
import axios from 'axios';
import Cookies from 'js-cookie';
import { toast } from 'react-toastify';

// Create axios instance with configuration
const api = axios.create({
    baseURL: 'https://praybenedictus.com/wp-json/ps/v1',
    timeout: 30000,
    headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
    },
    withCredentials: false // Keep this false to prevent CORS issues
});

//  **Token refresh control variables**
let isRefreshing = false;
let refreshSubscribers = [];

//  **Add request to queue during refresh process**
const subscribeToRefresh = (callback) => {
    refreshSubscribers.push(callback);
};

//  **Execute all queued requests after refresh completes**
const notifySubscribers = (newToken) => {
    refreshSubscribers.forEach((callback) => callback(newToken));
    refreshSubscribers = [];
};

//  **Get Auth Token (Check & Refresh if needed)**
export const getAuthToken = async () => {
    let token = Cookies.get("auth_token");
    let refreshToken = Cookies.get("refresh_token");

    //  If BOTH tokens are missing, redirect to login
    if (!token && !refreshToken) {
        console.warn(" No valid tokens found. Redirecting to login...");
        window.location.href = "/login";
        return null;
    }

    //  If auth token is missing but refresh token exists → Refresh it
    if (!token && refreshToken) {
        console.log(" Token expired. Attempting to refresh...");
        token = await refreshAuthToken();
    }

    return token || "";
};

//  **Refresh Auth Token (Debounced Requests)**
export const refreshAuthToken = async () => {
    const refreshToken = Cookies.get("refresh_token");

    if (!refreshToken) {
        console.warn(" No refresh token available.");
        window.location.href = "/login";
        return null;
    }

    //  Prevent multiple refresh calls
    if (isRefreshing) {
        return new Promise((resolve) => {
            subscribeToRefresh((newToken) => resolve(newToken));
        });
    }

    isRefreshing = true;

    try {
        console.log(" Attempting to refresh auth token...");

        const response = await axios.post(
            "https://praybenedictus.com/wp-json/ps/v1/auth/refresh",
            {},
            {
                headers: {
                    Authorization: `Bearer ${refreshToken}`,
                    Accept: "application/json",
                    "Content-Type": "application/json",
                },
            }
        );

        const { access_token, refresh_token: newRefreshToken } = response.data?.data?.attributes || {};

        if (access_token && newRefreshToken) {
            setTokens(access_token, newRefreshToken);
            console.log(" Tokens refreshed successfully.");

            notifySubscribers(access_token);
            return access_token;
        } else {
            throw new Error("No valid tokens received.");
        }
    } catch (error) {
        console.error(" Token refresh failed. Redirecting to login.");
        window.location.href = "/login";
        return null;
    } finally {
        isRefreshing = false;
    }
};


//  **Request Interceptor (Attach Token)**
api.interceptors.request.use(
    async (config) => {
        const token = await getAuthToken();
        if (token) {
            config.headers["Authorization"] = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

//  **Response Interceptor (Auto Refresh on Expired Token)**
api.interceptors.response.use(
    (response) => response, //  Pass successful responses through
    async (error) => {
        const originalRequest = error.config;

        console.error("API Error:", {
            url: originalRequest?.url,
            method: originalRequest?.method,
            status: error.response?.status,
            message: error.message,
        });

        //  **Handle request cancellation**
        if (axios.isCancel(error)) return Promise.reject(error);

        //  **Handle 401 Unauthorized (Token Expired)**
        if (error.response?.status === 401 && !originalRequest._retry) {
            originalRequest._retry = true; //  Prevents infinite loops

            const newToken = await refreshAuthToken(); //  **Triggers token refresh**

            if (newToken) {
                console.log(" Retrying request with new token.");
                originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
                return api(originalRequest); //  **Retries failed request**
            }
        }

        //  **Handle 503 Errors (Retry)**
        if (error.response?.status === 503 && !originalRequest._retry) {
            originalRequest._retry = true;
            return new Promise((resolve) => setTimeout(resolve, 2000)).then(() => api(originalRequest));
        }

        //  **Only redirect if BOTH tokens are missing**
        const token = Cookies.get("auth_token");
        const refreshToken = Cookies.get("refresh_token");

        if (!token && !refreshToken) {
            console.warn(" No valid tokens. Redirecting to login...");
            window.location.href = "/login"; //  Redirect only when confirmed
        }

        return Promise.reject(error);
    }
);

// Function to set tokens in cookies
export const setTokens = (accessToken, refreshToken) => {
    if (!accessToken || !refreshToken) {
        return;
    }

    Cookies.set('auth_token', accessToken, {
        expires: 45 / (60 * 24), // 45 minutes expiration
        path: '/',
        secure: window.location.protocol === 'https:',
        sameSite: 'Lax'
    });

    Cookies.set('refresh_token', refreshToken, {
        expires: 7, // 7 days expiration
        path: '/',
        secure: window.location.protocol === 'https:',
        sameSite: 'Lax'
    });

    console.log('Tokens set successfully in cookies.');
};


// Function to log in with email (Step 1)
export const loginUser = async (email) => {
    try {
        console.log(`Attempting to login with email: ${email}`);

        const response = await axios.post(
            'https://praybenedictus.com/wp-json/ps/v1/auth/login',
            { email },
            { headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' } }
        );

        console.log('Full API Login Response:', response.data);

        // Extract login_id from the correct structure
        const loginId = response?.data?.data?.id;

        if (loginId) {
            console.log(' 2FA Required. Showing 2FA popup with login_id:', loginId);
            return { requires2FA: true, login_id: loginId };
        }

        throw new Error('Unexpected response: No login_id received.');
    } catch (error) {
        console.error('Login Error:', error.response?.data || error.message);
        throw error;
    }
};



// Function to verify 2FA and store tokens
export const verifyTwoFACode = async (email, twoFACode) => {
    try {
        console.log(` Debug: Verifying 2FA for email: ${email}, 2FA Code: ${twoFACode}`);

        if (!email) {
            console.error(' Missing Email for 2FA Verification.');
            throw new Error('Email is required for 2FA verification.');
        }
        if (!twoFACode || twoFACode.trim() === '') {
            console.error(' Missing 2FA Code.');
            throw new Error('2FA code is required.');
        }

        const payload = {
            email: email.trim(),
            code: twoFACode.trim() // API expects `code`, not `two_fa_code`
        };

        console.log(' Sending request with payload:', payload);

        const response = await axios.post(
            'https://praybenedictus.com/wp-json/ps/v1/auth/login/verify',
            payload,
            { headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' } }
        );

        console.log(' API 2FA Verification Response:', response.data);

        // Extract tokens from `data.attributes`

        const accessToken = response?.data?.data?.attributes?.access_token;
        const refreshToken = response?.data?.data?.attributes?.refresh_token;

        if (accessToken && refreshToken) {
            setTokens(accessToken, refreshToken);
            console.log(' 2FA Successful. Tokens stored in cookies.');
            return { success: true };
        } else {
            console.error(' 2FA verification failed. No valid token received.', response.data);
            throw new Error('2FA verification failed. No token received.');
        }
    } catch (error) {
        console.error(' 2FA Verification Error:', error.response?.data || error.message);
        throw error;
    }
};

// Function to get user data using token from cookies
export const getUserData = async () => {
    try {
        const response = await api.get('/auth/user');
        return response.data;
    } catch (error) {
        console.error('GET User Data Error:', error.response?.data || error.message);
        throw error;
    }
};

// API service functions
export const fetchCatechesisData = async (signal) => {
    try {
        // Try to login first if no token exists
        if (!getAuthToken()) {
            await loginUser();
        }

        const response = await api.get('/post-types/catecheses', {
            signal,
            headers: {
                Authorization: `Bearer ${getAuthToken}`,
                Accept: 'application/json'
            }
        });

        return response?.data?.data || [];
    } catch (error) {
        if (axios.isCancel(error)) {
            console.log('Request canceled:', error.message);
            return [];
        }
        console.error('Error fetching catechesis data:', error);
        return [];
    }
};

export const fetchPrayerAndRitesData = async (signal, searchQuery = '') => {
    try {
        // Try to login first if no token exists
        if (!getAuthToken()) {
            await loginUser();
        }

        const response = await api.get('/post-types/prayer-rites', {
            signal,

            headers: {
                Authorization: `Bearer ${getAuthToken}`,
                Accept: 'application/json'
            },
            params: {
                'filter[search]': searchQuery,
            },
        });

        return response?.data?.data || [];
    } catch (error) {
        if (axios.isCancel(error)) {
            console.log('Request canceled:', error.message);
            return [];
        }
        console.error('Error fetching prayer data:', error);
        return [];
    }
};

export const fetchCultureAndCustomsData = async (page = 1, limit = 10) => {
    try {
        if (!getAuthToken()) {
            await loginUser();
        }

        console.log(`Fetching Page ${page} with limit ${limit}`);


        const response = await api.get(`/post-types/culture-customs?page=${page}&limit=${limit}`, {
            headers: {
                Authorization: `Bearer ${getAuthToken()}`,
                Accept: 'application/json'
            }
        });

        return response?.data?.data || [];
    } catch (error) {
        console.error('Error fetching culture data:', error);
        return [];
    }
};

export const fetchCultureAndCustomsListData = async (
    offset = 0,
    limit = 10,
    searchQuery = "",
    selectedCategory = "all",
    selectedSeason = "all"
) => {
    try {
        const token = await getAuthToken();
        if (!token) {
            console.warn(" No auth token found. Redirecting to login...");
            await loginUser();
        }

        console.log(`Fetching Data - Offset: ${offset}, Limit: ${limit}, Search: "${searchQuery}", Category ++ : "${selectedCategory}"`);

        let url = `/post-types/culture-customs?page[limit]=${limit}&page[offset]=${offset}&fields[ps-culture-customs]=title,slug,favorite,excerpt,featured_image,ps_content_type`;

        let filters = [];

        //  Apply category filter if it's not "all"
        if (selectedCategory !== "all") {
            filters.push(`filter[meta][ps_content_type]=${encodeURIComponent(selectedCategory)}`);
        }
        if (selectedSeason !== "all") {
            filters.push(`filter[taxonomy][ps-seasons]=${encodeURIComponent(selectedSeason)}`);
        }

        if (searchQuery.trim() !== "") {
            filters.push(`filter[search]=${encodeURIComponent(searchQuery)}`);
        }

        if (filters.length > 0) {
            url += `&${filters.join("&")}`;
        }
        console.log(" Final API URL For Culture:", url);

        const response = await api.get(url, {
            headers: {
                Authorization: `Bearer ${token}`,
                Accept: 'application/json'
            }
        });

        console.log(" API Response:", response.data);

        return {
            data: response?.data?.data || [],
            included: response?.data?.included || [],
            meta: response?.data?.meta || []
        };

    } catch (error) {
        console.error(" Error fetching Culture & Customs data:", error);
        return {
            data: [],
            included: [],
            meta: []
        };
    }
};




export const fetchCatchesisListData = async (offset = 0, limit = 10) => {
    try {
        const token = await getAuthToken();
        if (!token) {
            console.warn(" No auth token found. Redirecting to login...");
            await loginUser();
        }

        console.log(`Fetching Culture & Customs - Offset: ${offset}, Limit: ${limit}`);

        const response = await api.get(
            `/post-types/catecheses?page[limit]=${limit}&page[offset]=${offset}&fields[ps-culture-customs]=title,slug,favorite`,
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                    Accept: 'application/json'
                }
            }
        );

        return {
            data: response?.data?.data || [], //  Main records
            included: response?.data?.included || [], //  Taxonomies & Relationships
            meta: response?.data?.meta || [] //  Taxonomies & Relationships
        };

    } catch (error) {
        console.error(" Error fetching Culture & Customs data:", error);

        return {
            data: [],
            included: [],
            meta: []
        }; //  Prevent breaking the UI
    }
};


export const fetchPrayerRitesListData = async (
    offset = 0,
    limit = 10,
    searchQuery = "",
    selectedCategory = "all",
    selectedSeason = "all"
) => {
    try {
        const token = await getAuthToken();
        if (!token) {
            console.warn(" No auth token found. Redirecting to login...");
            await loginUser();
        }

        let url = `/post-types/prayer-rites?page[limit]=${limit}&page[offset]=${offset}&fields[ps-prayer-rites]=title,slug,excerpt,favorite,featured_image,ps_content_type`;

        let filters = [];

        if (selectedCategory !== "all") {
            filters.push(`filter[taxonomy][ps-prayer-types]=${encodeURIComponent(selectedCategory)}`);
        }

        if (selectedSeason !== "all") {
            filters.push(`filter[taxonomy][ps-seasons]=${encodeURIComponent(selectedSeason)}`);
        }

        if (searchQuery.trim() !== "") {
            filters.push(`filter[search]=${encodeURIComponent(searchQuery)}`);
        }

        if (filters.length > 0) {
            url += `&${filters.join("&")}`;
        }

        console.log(" Final API URL for Prayer Rites:", url);

        const response = await api.get(url, {
            headers: {
                Authorization: `Bearer ${token}`,
                Accept: 'application/json',
            },
        });

        return {
            data: response?.data?.data || [],
            included: response?.data?.included || [],
            meta: response?.data?.meta || {},
        };

    } catch (error) {
        console.error(" Error fetching Prayer Rites data:", error);
        return { data: [], included: [], meta: {} };
    }
};




export const fetchCatecheticalCourses = async (offset = 0, limit = 10, searchQuery = "") => {
    try {
        const token = await getAuthToken();
        if (!token) {
            console.warn(" No auth token found. Redirecting to login...");
            await loginUser();
        }

        // console.log(`Fetching Catechetical Courses - Offset: ${offset}, Limit: ${limit} , Search: "${searchQuery}"`);

        let url = `/taxonomies/catechetical-courses?page[limit]=${limit}&page[offset]=${offset}`;

        let filters = [];

        if (searchQuery.trim() !== "") {
            filters.push(`filter[search]=${encodeURIComponent(searchQuery)}`);
        }
        if (filters.length > 0) {
            url += `&${filters.join("&")}`;
        }

        const response = await api.get(url,

            {
                headers: {
                    Authorization: `Bearer ${token}`,
                    Accept: 'application/json'
                }
            }
        );

        return {
            data: response?.data?.data || [], //  Main course records
            included: response?.data?.included || [], //  Any included relationships
            meta: response?.data?.meta || {} //  Pagination metadata
        };

    } catch (error) {
        console.error(" Error fetching Catechetical Courses:", error);

        return {
            data: [],
            included: [],
            meta: {}
        }; //  Prevent breaking the UI
    }
};

export const fetchCatechesisList = async (slug, offset = 0, limit = 10) => {
    try {
        const token = await getAuthToken();
        if (!token) {
            console.warn(" No auth token found. Redirecting to login...");
            await loginUser();
        }

        let url = `/post-types/catecheses?page[limit]=${limit}&page[offset]=${offset}&fields[ps-catechesis]=title,slug,excerpt,favorite`;

        if (slug) {
            url += `&filter[taxonomy][ps-catechetical-courses]=${slug}`;
        }

        const response = await api.get(url, {
            headers: {
                Authorization: `Bearer ${token}`, //  Pass Auth Token
                Accept: 'application/json',
            },
        });

        return {
            data: response?.data?.data || [], //  Main course records
            included: response?.data?.included || [], //  Any included relationships
            meta: response?.data?.meta || {} //  Pagination metadata
        };
    } catch (error) {
        console.error(" Error fetching Catechetical Courses:", error);

        return {
            data: [],
            included: [],
            meta: {}
        }; //  Prevent breaking the UI
    }
};

export const fetchSingleCatecheticalCourse = async (slug) => {
    try {
        if (!slug) throw new Error(" Course SLug is required.");

        const token = await getAuthToken();
        if (!token) {
            console.warn(" No auth token found. Redirecting to login...");
            await loginUser();
        }

        const url = `/taxonomies/catechetical-courses/${slug}`;

        const response = await api.get(url, {
            headers: {
                Authorization: `Bearer ${token}`, //  Pass Auth Token
                Accept: 'application/json',
            },
        });

        return response.data?.data || null; //  Return the fetched course data
    } catch (error) {
        console.error(" Error fetching single catechetical course:", error);
        throw new Error("Failed to fetch the catechetical course.");
    }
};

export const fetchLiturgicalEvents = async (signal) => {
    try {
        // Try to login first if no token exists
        if (!getAuthToken()) {
            await loginUser();
        }

        const response = await api.get('/post-types/liturgical-events', {
            signal,
            headers: {
                Authorization: `Bearer ${getAuthToken}`,
                Accept: 'application/json',

            }
        });

        return response?.data?.data || [];
    } catch (error) {
        if (axios.isCancel(error)) {
            console.log('Request canceled:', error.message);
            return [];
        }
        console.error('Error fetching liturgical events:', error);
        return [];
    }
};

export const fetchLitCalMonth = async (year, month, signal) => {
    try {
        // Try to login first if no token exists
        if (!getAuthToken()) {
            await loginUser();
        }
        const url = `/litcal/${year}/${month}`;
        const headers = {
            Authorization: `Bearer ${getAuthToken()}`,
            Accept: 'application/json',
        };

        const response = await api.get(url, { signal, headers });


        return response?.data || {};
    } catch (error) {
        if (axios.isCancel(error)) {
            console.log('Request canceled:', error.message);
            return [];
        }
        console.error('Error fetching liturgical events:', error);
        return [];
    }
};

export const fetchRelatedLiturgicalData = async (postId) => {
    try {
        const token = await getAuthToken();
        if (!token) {
            await loginUser();
        }

        const response = await api.get(`/post-types/liturgical-events/${postId}/related`, {
            headers: {
                Authorization: `Bearer ${token}`,
                Accept: 'application/json',
            }
        });

        return response?.data?.data || [];
    } catch (error) {
        if (axios.isCancel(error)) {
            console.log('Request canceled:', error.message);
            return [];
        }
        console.error('Error fetching related liturgical data:', error);
        return [];
    }
};


// FOR SINGLE PAGES - USING ID

// Function to fetch a single culture-custom item by ID
export const fetchSingleCultureCustoms = async (slug, signal) => {
    try {

        // Validate ID 
        if (!slug) {
            console.error('No ID provided for culture-customs fetch');
            return null;
        }
        if (!getAuthToken()) {
            await loginUser();
        }

        const response = await api.get(`/post-types/culture-customs/${slug}`, {
            signal,
            headers: {
                Authorization: `Bearer ${getAuthToken()}`,
                Accept: 'application/json'
            }
        });

        return response?.data || null;
    } catch (error) {
        console.error(`Error fetching culture-customs data for ID ${slug}:`, error);
        return null;
    }
};
// export const fetchRelatedCultureCustoms = async (id, signal) => {
//     try {
//       const response = await api.get(`/post-types/culture-customs/${id}/related`, {
//         signal,
//         headers: {
//           Authorization: `Bearer ${getAuthToken()}`,
//           Accept: 'application/json',
//         },
//       });
  
//       return response?.data || null;
//     } catch (error) {
//       console.error(`Error fetching related content for ID ${id}:`, error);
//       return null;
//     }
//   };
  

export const fetchSinglePrayerRite = async (slug, options = {}) => {
    try {
        // Validate ID
        if (!slug) {
            console.error('No ID provided for prayer-rites fetch');
            return null;
        }

        // Check for authentication if needed
        if (!getAuthToken()) {
            await loginUser();
        }

        // Extract signal from options if it exists
        const { signal } = options;

        // Use the api instance that you're using for other requests
        const response = await api.get(`/post-types/prayer-rites/${slug}`, {
            signal,
            headers: {
                Authorization: `Bearer ${getAuthToken()}`,
                Accept: 'application/json'
            }
        });

        return response?.data || null;
    } catch (error) {
        console.error(`Error fetching prayer-rite data for ID ${slug}:`, error);
        return null;
    }
};


export const fetchSingleCatechesis = async (slug, signal) => {
    try {
        // Validate ID before making a request
        if (!slug) {
            console.error('No ID provided for catechesis fetch');
            return null;
        }

        //  Check if the request is already aborted before making a request
        if (signal.aborted) {
            console.warn(`Request aborted before execution for catechesis ID ${slug}`);
            return null;
        }

        // Ensure authentication before making the request
        if (!getAuthToken()) {
            await loginUser();
        }

        // Make API request
        const response = await api.get(`/post-types/catecheses/${slug}`, {
            signal,
            headers: {
                Authorization: `Bearer ${getAuthToken()}`,
                Accept: 'application/json'
            }
        });

        // Return the fetched data or null if response is empty
        return response?.data || null;
    } catch (error) {
        // Handle request errors
        if (error.name === 'AbortError') {
            console.log(`Fetch request for catechesis ID ${slug} was aborted.`);
        } else {
            console.error(`Error fetching catechesis data for ID ${slug}:`, error);
        }
        return null;
    }
};

// Add this function to your api.js file
export const fetchLiturgicalEventsByDate = async (year, month, day, signal) => {
    try {
        // Try to login first if no token exists
        if (!getAuthToken()) {
            await loginUser();
        }

        const url = `/litevent/date/${year}/${month}/${day}`;
        console.log("Fetching URL : ", url);


        const response = await api.get(url, {
            signal,
            headers: {
                Authorization: `Bearer ${getAuthToken()}`,
                Accept: 'application/json'
            }
        });
        console.log("APi Response for litevent  ====>>>>>  : ", response.data);

        return response?.data || {};
    } catch (error) {
        if (axios.isCancel(error)) {
            console.log('Request canceled:', error.message);
            return {};
        }
        console.error(`Error fetching liturgical events for date ${year}/${month}/${day}:`, error);
        return {};
    }
};

//  **Fetch Recent Favorites**
export const fetchRecentFavorites = async () => {
    try {
        const response = await api.get("/auth/profile/favorites?page[limit]=5");
        return response?.data?.data || [];
    } catch (error) {
        console.error(" Error fetching recent favorites:", error.response?.data || error.message);
        return [];
    }
};

export const fetchFavorites = async () => {
    try {
        const response = await api.get("/auth/profile/favorites?page[limit]=50");

        return {
            data: response?.data?.data || [],
            included: response?.data?.included || [],
        };
    } catch (error) {
        console.error(" Error fetching favorites:", error.response?.data || error.message);
        return { data: [], included: [] };
    }
};



// export const fetchFavorites = async () => {
//     try {
//       const response = await api.get(
//         "/auth/profile/favorites?page[limit]=50&include=ps-seasons,ps-nationalities,ps-categories"
//       );
//       return {
//         data: response?.data?.data || [],
//         included: response?.data?.included || [],
//       };
//     } catch (error) {
//       console.error("Error fetching favorites:", error.response?.data || error.message);
//       return { data: [], included: [] };
//     }
//   };



// export const fetchFavorites = async () => {
//     try {
//         const response = await api.get("/auth/profile/favorites?page[limit]=50");

//         return {
//             data: response?.data?.data || [],
//             included: response?.data?.included || []
//         };

//     } catch (error) {
//         console.error("Error fetching favorites:", error.response?.data || error.message);
//         return {
//             data: [],
//             included: []
//         };
//     }
// };


export const fetchUserProfile = async () => {
    try {
        const response = await api.get("/auth/profile");
        const attributes = response.data?.data?.attributes || {};

        const first_name = attributes?.first_name?.trim() || "";
        const last_name = attributes?.last_name?.trim() || "";
        const email = attributes?.email?.trim() || "";
        const name = attributes?.name?.trim() || "User"; //  Ensure `name` has a default fallback

        const fullName = first_name && last_name ? `${first_name} ${last_name}` : name;

        //  Short Name Logic (Preventing Undefined Errors)
        let shortName;
        if (first_name && last_name) {
            shortName = `${first_name.charAt(0)}${last_name.charAt(0)}`.toUpperCase();
        } else if (first_name || last_name) {
            shortName = (first_name || last_name).substring(0, 2).toUpperCase();
        } else {
            shortName = name.substring(0, 2).toUpperCase(); //  Safe fallback
        }

        return { fullName, shortName, email, first_name, last_name };
    } catch (error) {
        console.error(" Error fetching user profile:", error);
        return {
            fullName: "Unknown User", shortName: "??", first_name: "",
            last_name: "",
            email: ""
        }; //  Prevent breaking UI
    }
};

export const fetchSubscriptions = async () => {
    try {
        const response = await api.get("/subscriptions");
        return response.data?.data || [];
    } catch (error) {
        console.error("Failed to fetch subscriptions:", error);
        return [];
    }
};

export const fetchNationalityName = async (id) => {
    try {
        const response = await api.get(`/taxonomies/nationalities/${id}`);
        return response.data?.data?.attributes?.name || "Culture";
    } catch (error) {
        console.error(`Error fetching nationality for ID ${id}:`, error);
        return "Culture"; // Default value in case of error
    }
};


export const fetchSeasonName = async (id) => {
    try {
        const response = await api.get(`/taxonomies/seasons/${id}`);
        return response.data?.data?.attributes?.name || "Season";
    } catch (error) {
        console.error(`Error fetching season for ID ${id}:`, error);
        return "Season"; // Default value
    }
};

export const fetchAllSeasons = async () => {

    try {
        const response = await api.get(`/taxonomies/seasons/`);
        return response.data?.data || {};
    } catch (error) {
        console.error(`Error fetching season:`, error);
        return "Season"; // Default value
    }
}

export const fetchAllPrayerTypes = async () => {

    try {
        const response = await api.get(`/taxonomies/prayer-types/`);
        return response.data?.data || {};
    } catch (error) {
        console.error(`Error fetching prayer types:`, error);
        return "prayer-types"; // Default value
    }
}

// Function to toggle favorite status
export const toggleFavorite = async (contentType, id, isTaxonomy = false, currentFavorite = false) => {
    console.log("Toggling favorite for ID:", id);
    try {
        const base = isTaxonomy ? 'taxonomies' : 'post-types';
        // Display toast before the API call based on the current state
        if (currentFavorite) {
            toast.success("Removed from favorites", {
                icon: <span className="custom-toast-icon success" />,
            });

        } else {
            toast.success("Added to favorites", {
                icon: <span className="custom-toast-icon success" />,
            });


        }
        const response = await api.get(`/${base}/${contentType}/${id}/favorite`);

        return Boolean(response.data?.favorite);
    } catch (error) {
        console.error(`Error toggling favorite for ID ${id}:`, error);
        throw error; // Handle error in UI
    }
};

export const fetchUpcomingHolyDays = async (count = 6) => {
    try {
        const response = await api.get(`/holydays/${count}`);
        return response?.data || [];
    } catch (error) {
        console.error('Error fetching upcoming holy days:', error);
        throw error;
    }
};

export const fetchDevotions = async (count = 2, filter = {}) => {

    const { date } = filter;

    try {
        let url = `/post-types/devotions/?fields[ps-devotions]=slug,favorite,title,ps_latin_short_audio_url,ps_latin_long_audio_url,english_short_audio_url,english_long_audio_url`;
        if (date) {
            url += `&filter[meta][ps_devotion_date]=${date}`;
        }
        const response = await api.get(url);
        return response?.data || [];
    } catch (error) {
        console.error('Error fetching upcoming holy days:', error);
        throw error;
    }
};

export const fetchSingleDevotion = async (slug) => {
    try {

        // Validate ID 
        if (!slug) {
            console.error('No Slug provided for single devotion fetch');
            return null;
        }
        if (!getAuthToken()) {
            await loginUser();
        }

        const response = await api.get(`/post-types/devotions/${slug}`, {
            headers: {
                Authorization: `Bearer ${getAuthToken()}`,
                Accept: 'application/json'
            }
        });

        return response?.data || null;
    } catch (error) {
        console.error(`Error fetching single devotion data for Slug ${slug}:`, error);
        return null;
    }
};

export const fetchSingleMeditation = async (id) => {
    try {

        // Validate ID 
        if (!id) {
            console.error('No id provided for single meditation fetch');
            return null;
        }
        if (!getAuthToken()) {
            await loginUser();
        }

        // const encodedid = encodeURIComponent(id);

        const response = await api.get(`/post-types/meditations/${id}`, {
            headers: {
                Authorization: `Bearer ${getAuthToken()}`,
                Accept: 'application/json'
            }
        });

        return response?.data || null;
    } catch (error) {
        console.error(`Error fetching single meditation data for id ${id}:`, error);
        return null;
    }
};




// Export the api instance as default
export default api;